import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { Box, CircularProgress } from "@mui/material";
import DeveloperCompanyProfile from "./components/DeveloperCompanyProfile";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SidebarCountsProvider } from "./context/SidebarCountsContext";
import LoginPage from "./layouts/auth/login";
import ResetPassword from "./layouts/auth/reset-password";
import Categories from "./layouts/categories";
import Contents from "./layouts/contents";
import Dashboard from "./layouts/dashboard";
import Forums from "./layouts/forums";
import NotFoundPage from "./layouts/not-found";
import Permissions from "./layouts/permissions";
import Polls from "./layouts/polls";
import Roles from "./layouts/roles";
import RootLayout from "./layouts/rootLayout";
import ServiceCenters from "./layouts/serviceCenters";
import ServiceCenterTypes from "./layouts/serviceCenterTypes";
import Settings from "./layouts/settings";
import Support from "./layouts/support";
import SupportCategories from "./layouts/supportCategories";
import Users from "./layouts/user";
import UserQuestions from "./layouts/userQuestions";

function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    // Show a loading spinner while the app is checking authentication
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
}

const ProtectedRoute = ({ permission, children }) => {
  const { permissions } = useAuth();

  return permissions.includes(permission) ? (
    children
  ) : (
    <Navigate to="/dashboard" />
  );
};

const App = () => {
  return (
    <AuthProvider>
      <SidebarCountsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/" element={<RootLayout />}>
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/developer-company"
                element={
                  <RequireAuth>
                    <DeveloperCompanyProfile />
                  </RequireAuth>
                }
              />
              <Route
                path="/categories"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="category-list">
                      <Categories />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/service-center-types"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="service-type-list">
                      <ServiceCenterTypes />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/users"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="user-list">
                      <Users />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/supports"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="support-list">
                      <Support />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/support-categories"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="support-category-list">
                      <SupportCategories />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/contents"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="content-list">
                      <Contents />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/forums"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="forum-list">
                      <Forums />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/questions"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="question-list">
                      <UserQuestions />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/polls"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="poll-list">
                      <Polls />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/service-centers"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="centers-list">
                      <ServiceCenters />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/roles"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="role-list">
                      <Roles />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/permissions"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="permission-list">
                      <Permissions />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <ProtectedRoute permission="manage-settings">
                      <Settings />
                    </ProtectedRoute>
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </SidebarCountsProvider>
    </AuthProvider>
  );
};

export default App;
